import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificationCard } from "@/components/NotificationCard";
import { useNotifications } from "@/hooks/useNotifications";
import { notificationHref } from "@/lib/notifications";

/**
 * Compact bell dropdown for the topbar. Shows the 5 most recent notifications,
 * a live unread count badge, and a "View all" link.
 *
 * `role` — "admin" | "agent" — controls where clicks route to.
 */
export function NotificationBell({ role = "admin" }) {
  const nav = useNavigate();
  const { items, freshIds, markRead } = useNotifications();
  const [open, setOpen] = useState(false);

  const unread = useMemo(() => items.filter((n) => !n.read).length, [items]);
  const preview = useMemo(() => items.slice(0, 5), [items]);
  const viewAllHref = role === "admin" ? "/admin/notifications" : "/app/notifications";

  const onCardClick = (n) => {
    setOpen(false);
    if (!n.read) markRead(n.id);
    const href = notificationHref(n, role);
    if (href) nav(href);
    else nav(viewAllHref);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`${role}-bell-btn`}
          aria-label="Notifications"
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-foreground transition hover:bg-accent"
        >
          <BellRing className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid={`${role}-bell-unread-badge`}
              className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-background"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[360px] p-0"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <div className="font-display text-sm font-bold">Notifications</div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {unread > 0 ? `${unread} unread` : "You're all caught up"}
            </div>
          </div>
        </div>
        <div className="max-h-[360px] space-y-1.5 overflow-y-auto p-2">
          {preview.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            preview.map((n) => (
              <NotificationCard
                key={n.id}
                n={n}
                dense
                slide={freshIds.has(n.id)}
                onClick={() => onCardClick(n)}
              />
            ))
          )}
        </div>
        <button
          data-testid={`${role}-bell-view-all-btn`}
          onClick={() => { setOpen(false); nav(viewAllHref); }}
          className="block w-full border-t border-border py-2.5 text-center text-xs font-semibold text-primary hover:bg-accent"
        >
          View all →
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
